import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Coins, ImageIcon, KeyRound, Languages, Mic } from 'lucide-react';

const PANEL_AGENT_KEYS = ['support', 'sales', 'knowledge'] as const;

export const LandingHeroPanel: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="hidden rounded-lg border border-white/10 bg-[#0d1728]/80 shadow-2xl shadow-black/40 backdrop-blur lg:block">
      <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
        <div>
          <div className="text-xs uppercase text-teal-200">{t('landing.panel.workspace')}</div>
          <div className="mt-1 text-lg font-semibold">{t('landing.panel.title')}</div>
        </div>
        <div className="rounded-lg border border-emerald-300/30 bg-emerald-400/10 px-3 py-1 text-xs text-emerald-100">
          {t('landing.panel.guard')}
        </div>
      </div>
      <div className="grid gap-4 p-5 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-3">
          {PANEL_AGENT_KEYS.map((key) => (
            <div key={key} className="rounded-lg border border-white/10 bg-white/[0.04] p-4">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-teal-400/15 text-teal-200">
                  <Bot size={18} />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">
                    {t(`landing.panel.agents.${key}.name`)}
                  </div>
                  <div className="text-xs text-slate-400">
                    {t(`landing.panel.agents.${key}.meta`)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-lg border border-white/10 bg-[#07111f]/70 p-4">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold">
            <Coins size={16} className="text-amber-300" />
            {t('landing.panel.credits')}
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-slate-400">{t('landing.panel.packageCredits')}</span>
              <span className="text-right text-teal-200">
                {t('landing.panel.packageCreditsValue')}
              </span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-slate-400">{t('landing.panel.advanceCredits')}</span>
              <span className="text-right text-amber-200">
                {t('landing.panel.advanceCreditsValue')}
              </span>
            </div>
          </div>
          <div className="mt-5 h-2 overflow-hidden rounded-full bg-white/10">
            <div className="h-full w-3/4 rounded-full bg-teal-300" />
          </div>
          <div className="mt-5 rounded-lg border border-sky-300/20 bg-sky-400/10 p-3 text-xs text-sky-100">
            {t('landing.panel.quotaNote')}
          </div>
          <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.04] p-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-200">
              <KeyRound size={14} className="text-teal-200" />
              {t('landing.panel.apiKey')}
            </div>
            <div className="grid gap-2 text-xs text-slate-300">
              <div className="flex items-center gap-2">
                <Languages size={14} className="text-indigo-200" />
                {t('landing.panel.translation')}
              </div>
              <div className="flex items-center gap-2">
                <ImageIcon size={14} className="text-amber-200" />
                {t('landing.panel.imageGeneration')}
              </div>
              <div className="flex items-center gap-2">
                <Mic size={14} className="text-rose-200" />
                {t('landing.panel.voice')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
